<template>

        <div class="titolo-card">
          <button class="btn primary staff-button-positive" @click="goToManualOrderPage">Nuovo ordine manuale</button>
        </div>

        <table class="orders-table" v-if="orders.length">
          <thead>
            <tr>
              <th>ID Ordine</th>
              <th>Data</th>
              <th>Stato</th>
              <th>Pagamento</th>
              <th>Totale</th>
              <th>Prodotti</th>
              <th>Note</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="order in orders" :key="order._id || order.orderId">
            <tr>
              <td><strong>#{{ order.orderId || order._id }}</strong></td>
              <td>{{ formatDate(order.orderDate) }}</td>
              <td><span :class="'badge ' + statusClass(order.status)">{{ formatStatus(order.status) }}</span></td>
              <td>
                <div>{{ formatLabel(order.paymentMethod || order.paymentProvider) }}</div>
                <span v-if="hasManualInstallments(order)" class="manual-summary">
                  {{ manualInstallmentsSummary(order.manualInstallments, order) }}
                </span>
              </td>
              <td>{{ formatPrice(order.totalPrice) }}</td>
              <td>
                <ul>
                  <li v-for="(product, idx) in (order.products || [])" :key="product.id || product.sku || product.name || idx">
                    <strong>{{ product.name || product.id || 'Prodotto' }}</strong><br />
                    ID: {{ product.id || product.sku || '—' }}<br />
                    Quantità: {{ product.quantity || 1 }} - {{ formatPrice(product.price) }}
                  </li>
                </ul>
              </td>
              <td class="note-cell">
                <span :title="order.notes || '—'">
                  {{ order.notes || '—' }}
                </span>
              </td>
              <td class="actions-cell">
                <button class="btn ghost tiny" @click="goToOrderManager(order.userEmail)">
                  Gestisci ordine
                </button>
              </td>
            </tr>
            <tr v-if="hasManualInstallments(order)" class="installments-detail-row">
              <td colspan="8">
                <div class="manual-plan">
                  <div class="manual-plan__header">
                    <strong>Rate manuali</strong>
                    <span>{{ manualInstallmentsSummary(order.manualInstallments, order) }}</span>
                  </div>
                  <div class="manual-installments-grid">
                    <div class="manual-installments-grid__head">
                      <span>#</span>
                      <span>Importo</span>
                      <span>Metodo</span>
                      <span>Scadenza</span>
                      <span>Stato</span>
                      <span>Allegato</span>
                    </div>
                    <div
                      v-for="(inst, idx) in order.manualInstallments"
                      :key="inst.id || idx"
                      class="manual-installments-grid__row"
                      :class="{ overdue: isManualInstallmentExpired(inst, order) }"
                    >
                      <span>#{{ idx + 1 }}</span>
                      <span>{{ formatPrice(inst.amount) }}</span>
                      <span>{{ formatLabel(inst.method) }}</span>
                      <span>{{ inst.scheduledDate ? formatDate(inst.scheduledDate) : '—' }}</span>
                      <span>
                        <span class="rate-chip" :class="'rate-chip--' + statusClass(manualInstallmentDisplayStatus(inst, order))">
                          {{ formatStatus(manualInstallmentDisplayStatus(inst, order)) }}
                        </span>
                      </span>
                      <span>
                        <a
                          v-if="manualInstallmentProof(inst, order)?.url"
                          :href="manualInstallmentProof(inst, order).url"
                          target="_blank"
                          rel="noopener"
                          class="proof-link"
                        >
                          {{ proofDisplayLabel(manualInstallmentProof(inst, order)) }}
                        </a>
                        <span v-else-if="isPaymentProofMethod(inst.method)" class="muted">Allegato mancante</span>
                        <span v-else class="muted">—</span>
                      </span>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            </template>
          </tbody>
        </table>

        <p v-else>Nessun ordine trovato.</p>
</template>


<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import {
  deriveManualInstallmentStatus,
  isManualInstallmentOperationallyExpired,
  isManualPaymentProofMethod,
  manualInstallmentPaymentProof,
  manualInstallmentProofLabel,
  manualInstallmentStats,
  normalizeManualInstallmentMethod,
} from '@/lib/manualInstallments';

export default {
  props: {
    userId: String
  },
  setup(props) {
    const router = useRouter();
    const db = getFirestore();
    const orders = ref([]);
    const userEmail = ref('');
    const userName = ref('');
    const userSurname = ref('');

    const fetchUserEmail = async () => {
      try {
        const docRef = doc(db, "users", props.userId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          userEmail.value = data.email || '';
          userName.value = data.name || '';
          userSurname.value = data.surname || '';
        } else {
          console.warn("Utente non trovato");
        }
      } catch (err) {
        console.error("Errore nel recupero email:", err);
      }
    };

    const fetchOrders = async () => {
      if (!userEmail.value) return;

      try {
        const ordersQuery = query(
          collection(db, "orders"),
          where("userEmail", "==", userEmail.value)
        );
        const querySnapshot = await getDocs(ordersQuery);
        orders.value = querySnapshot.docs
          .map(docSnap => ({ _id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
      } catch (err) {
        console.error("Errore nel recupero ordini:", err);
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '—';
      const date = new Date(dateString);
      return Number.isNaN(date.getTime()) ? dateString : date.toLocaleString("it-IT");
    };

    const formatPrice = (value) => {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return '—';
      return amount.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    };

    const formatLabel = (value) => {
      if (!value) return '—';
      return value
        .toString()
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    };

    const formatStatus = (status) => (
      String(status || '').trim().toLowerCase() === 'annullata'
        ? 'Annullata / non dovuta'
        : formatLabel(status || 'Unknown')
    );

    const statusClass = (status) => {
      return (status || 'unknown')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
    };

    const hasManualInstallments = (order) => {
      return Array.isArray(order?.manualInstallments) && order.manualInstallments.length > 0;
    };

    const normalizeMethod = (value) => normalizeManualInstallmentMethod(value);
    const isPaymentProofMethod = (value) => isManualPaymentProofMethod(value);

    const isManualInstallmentExpired = (inst, order = {}) => {
      return isManualInstallmentOperationallyExpired(inst, Date.now(), order);
    };

    const manualInstallmentDisplayStatus = (inst, order = {}) => {
      return isManualInstallmentExpired(inst, order)
        ? 'scaduta'
        : deriveManualInstallmentStatus(inst, Date.now(), order);
    };

    const manualInstallmentProof = (inst, order) => {
      return manualInstallmentPaymentProof(inst, order);
    };

    const proofDisplayLabel = (proof) => manualInstallmentProofLabel(proof);

    const manualInstallmentsSummary = (plan = [], order = {}) => {
      const stats = manualInstallmentStats(plan, Date.now(), order);
      if (!stats.total) return '—';
      const parts = [`${stats.total} ${stats.total === 1 ? 'rata' : 'rate'}`];
      parts.push(`${stats.paid}/${stats.total} pagate`);
      if (stats.expired) parts.push(`${stats.expired} ${stats.expired === 1 ? 'scaduta' : 'scadute'}`);
      if (stats.pending) parts.push(`${stats.pending} in attesa`);
      if (stats.cancelled) parts.push(`${stats.cancelled} ${stats.cancelled === 1 ? 'annullata' : 'annullate'}`);
      return parts.join(' · ');
    };

    const loadData = async () => {
      await fetchUserEmail();
      await fetchOrders();
    };

    const goToManualOrderPage = () => {
      const query = {
        userId: props.userId || '',
        email: userEmail.value || '',
        name: userName.value || '',
        surname: userSurname.value || ''
      };

      Object.keys(query).forEach((key) => {
        if (!query[key]) delete query[key];
      });

      router.push({
        path: '/order-list-new',
        query
      });
    };

    const goToOrderManager = (email = '') => {
      const query = {};
      const targetEmail = (email || userEmail.value || '').trim();
      if (targetEmail) query.searchEmail = targetEmail;

      router.push({
        path: '/order-list-new',
        query
      });
    };

    onMounted(loadData);

    return {
      orders,
      userEmail,
      userName,
      userSurname,
      formatDate,
      formatPrice,
      formatLabel,
      formatStatus,
      statusClass,
      hasManualInstallments,
      normalizeMethod,
      isPaymentProofMethod,
      isManualInstallmentExpired,
      manualInstallmentDisplayStatus,
      manualInstallmentProof,
      proofDisplayLabel,
      manualInstallmentsSummary,
      goToManualOrderPage,
      goToOrderManager
    };
  }
};
</script>



<style scoped>
.orders-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 10px;
  margin-top: 20px;
  font-size: 15px;
}

.orders-table th {
  text-align: left;
  background-color: #f9f9f9;
  padding: 12px;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #ddd;
}

.orders-table td {
  background-color: #fff;
  padding: 12px;
  vertical-align: top;
  border-bottom: 1px solid #eee;
}

.orders-table td ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.orders-table td ul li {
  background: #f6f8fa;
  border: 1px solid #dfe3e8;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 8px;
  margin-top: 0px;
  font-size: 14px;
  color: #222;
  line-height: 1.4;
}

.orders-table td ul li strong {
  display: block;
  color: #000;
  font-weight: 600;
  margin-bottom: 4px;
}

.manual-summary {
  display: inline-block;
  margin-top: 6px;
  color: #475569;
  font-size: 12px;
  line-height: 1.35;
}

.installments-detail-row td {
  padding-top: 0;
}

.manual-plan {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
}

.manual-plan__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  color: #0f172a;
}

.manual-plan__header span {
  color: #475569;
  font-size: 13px;
}

.manual-installments-grid {
  display: grid;
  gap: 6px;
}

.manual-installments-grid__head,
.manual-installments-grid__row {
  display: grid;
  grid-template-columns: 44px minmax(90px, 0.8fr) minmax(90px, 0.9fr) minmax(130px, 1.2fr) minmax(120px, 1fr) minmax(140px, 1.4fr);
  gap: 10px;
  align-items: center;
}

.manual-installments-grid__head {
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.manual-installments-grid__row {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
}

.manual-installments-grid__row.overdue {
  border-color: #fecaca;
  background: #fff7f7;
}

.rate-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  background: #e0e7ff;
  color: #312e81;
}

.rate-chip--pagato {
  background: #dcfce7;
  color: #166534;
}

.rate-chip--in-attesa-di-pagamento,
.rate-chip--pending {
  background: #fef3c7;
  color: #92400e;
}

.rate-chip--scaduta {
  background: #fee2e2;
  color: #991b1b;
}

.rate-chip--annullata {
  background: #e2e8f0;
  color: #475569;
}

.proof-link {
  color: #2563eb;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  word-break: break-word;
}

.proof-link:hover {
  text-decoration: underline;
}

.muted {
  color: #64748b;
  font-size: 12px;
}

.badge {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
    background-color: #747474;
  text-transform: uppercase;
}

.badge.completed {
  background-color: #28a745;
}


.badge.wc-completed {
  background-color: #28a745;
}

.badge.wc-in-spedizione {
  background-color: #28a745;
}

.badge.wc-processing {
  background-color: #ffc107;
  color: #222;
}
.badge.wc-on-hold {
  background-color: #6c757d;
}

.badge.cancelled {
  background-color: #dc3545;
}

.badge.wc-cancelled {
  background-color: #dc3545;
}

.badge.in-attesa-di-pagamento,
.badge.pending {
  background-color: #ffc107;
  color: #222;
}

.badge.pagato,
.badge.rateizzato {
  background-color: #28a745;
}

.badge.scaduta,
.badge.scaduto {
  background-color: #dc3545;
}

.titolo-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.btn.primary {
  background-color: #2563eb;
  color: #fff;
}

.btn.primary:hover {
  background-color: #1e4ec2;
}

.btn.ghost {
  background-color: transparent;
  border-color: #d0d7e2;
  color: #334155;
}

.btn.ghost:hover {
  background-color: #f1f5f9;
}

.btn.tiny {
  padding: 6px 10px;
  font-size: 0.78rem;
  border-radius: 8px;
}

.actions-cell {
  vertical-align: middle;
  text-align: center;
}

.note-cell {
  max-width: 220px;
  font-size: 14px;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 900px) {
  .manual-plan__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .manual-installments-grid__head {
    display: none;
  }

  .manual-installments-grid__row {
    grid-template-columns: 1fr;
  }
}
</style>
